import React, { FC, isValidElement, useLayoutEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { OnClick } from '@rbi-ctg/frontend';

import { IActive, ITabsStyle, SlidingUnderline, TabStyle } from './styled';

type ColorOptions = 'primary' | 'secondary' | 'inverted';
interface ITabs {
  color?: ColorOptions;
  selectedIndex: number;
  onChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
  children: React.ReactNode[];
  inactiveStyle?: { [key: string]: string | number };
  slidingUnderline?: boolean;
  slidingUnderlineColor?: ColorOptions;
}

export const TabsStyle = styled.div<ITabsStyle>`
  display: flex;
  flex-direction: row;
  width: ${p => (p.tabWidth === 'fill' ? '100%' : 'auto')};
  margin: 0;
  padding: 0;
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const Tabs: FC<ITabs & ITabsStyle> = ({
  selectedIndex,
  onChange,
  children,
  color,
  inactiveStyle,
  tabWidth = 'fill',
  slidingUnderline = false,
  slidingUnderlineColor
}) => {
  const container = useRef<null | HTMLElement>();
  const [containerWidth, setContainerWidth] = useState(0);
  const enableSlidingUnderline = tabWidth === 'fill' && slidingUnderline;

  useLayoutEffect(() => {
    if (enableSlidingUnderline) {
      setContainerWidth(container.current?.getBoundingClientRect()?.width ?? 0);
    }
  }, [enableSlidingUnderline]);

  if (!children || children.length <= 0) {
    return null;
  }
  const tabSlideWidth = containerWidth / children.length;

  const tabs = children.map(child => {
    if (!isValidElement(child)) {
      return child;
    }

    const handleClick = (e: React.ChangeEvent<{}>) => {
      onChange(e, child.props.value);
    };

    return React.cloneElement(child, {
      color,
      id: child.props.id,
      inactiveStyle,
      key: `tab_${child.props.value}`,
      active: !enableSlidingUnderline && child.props.value === selectedIndex,
      ariaControls: child.props.ariaControls,
      onClick: handleClick,
      tabWidth: child.props.tabWidth || tabWidth
    });
  });

  return (
    <TabContainer
      ref={ref => {
        if (ref) {
          container.current = ref;
        }
      }}
    >
      <TabsStyle tabWidth={tabWidth}>{tabs}</TabsStyle>
      {enableSlidingUnderline && (
        <SlidingUnderline
          tabSlideWidth={tabSlideWidth}
          idx={selectedIndex}
          color={slidingUnderlineColor || color}
        />
      )}
    </TabContainer>
  );
};

interface ITab {
  id?: string;
  'data-testid'?: string;
  inactiveStyle?: { [key: string]: string | number };
  active?: boolean;
  label: string;
  value?: number;
  onClick?: OnClick;
  ariaControls?: string;
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'inverted';
}

export const Tab: FC<ITab & ITabsStyle> = ({
  id,
  'data-testid': dataTestId,
  label,
  active,
  disabled,
  color,
  onClick,
  ariaControls,
  inactiveStyle,
  tabWidth
}) => {
  return (
    <TabStyle
      id={id}
      data-testid={dataTestId}
      role="tab"
      aria-selected={active}
      aria-controls={ariaControls}
      color={color}
      disabled={disabled}
      active={active}
      onClick={onClick}
      inactiveStyle={inactiveStyle}
      tabWidth={tabWidth}
    >
      {label}
    </TabStyle>
  );
};

interface ITabPanel {
  id: string;
  value: number;
  index?: number;
  children: React.ReactNode;
  ariaLabeledBy: string;
}

const TabPanelStyle = styled.div<IActive>`
  display: ${p => (p.active ? 'flex' : 'none')};
  position: relative;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: center;
  :focus {
    outline: 0;
  }
  flex: 1;
`;

export const TabPanel: FC<ITabPanel> = ({ id, value, index, children, ariaLabeledBy }) => {
  const hidden = value !== index;
  return (
    <TabPanelStyle
      id={id}
      key={`tab-panel_${id}`}
      hidden={hidden}
      active={!hidden}
      role="tabpanel"
      tabIndex={-1}
      aria-labelledby={ariaLabeledBy}
    >
      {children}
    </TabPanelStyle>
  );
};
